import { useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { rispondiConsultazione } from '../../api/consultazioni';
import type { ConsultazioneItem, RichiestaDetail } from '../../api/richieste';

interface Props {
  consultazione: ConsultazioneItem;
  onClose: () => void;
  onSaved: (updated: RichiestaDetail) => void;
}

const inputCls =
  'w-full text-sm border border-stone-border rounded px-3 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

/**
 * Risposta a una consultazione aperta. RT solo tipo=RT, DIR solo tipo=DIR;
 * admin sempre. Chiude la consultazione e riporta l'ordine al
 * `stato_pre_consultazione`.
 */
export default function RispondiConsultazioneModal({ consultazione, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [commento, setCommento] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleRispondi() {
    if (!commento.trim()) {
      setError(t('richieste.rispondi.val_risposta'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const updated = await rispondiConsultazione(consultazione.id, {
        commento_risposta: commento.trim(),
      });
      onSaved(updated);
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { detail?: string } } })?.response;
      setError(resp?.data?.detail ?? t('richieste.rispondi.errore'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark">
            {t('richieste.rispondi.titolo', { tipo: consultazione.tipo })}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="px-3 py-2 bg-stone-bg/60 border border-stone-mid rounded">
            <p className="text-[11px] font-semibold text-ink-soft uppercase tracking-wide mb-1">
              {t('richieste.rispondi.richiesta_da', { nome: consultazione.richiesta_da_nome ?? t('richieste.workflow.nd') })}
            </p>
            <p className="text-sm text-ink-mid whitespace-pre-wrap">
              {consultazione.commento_richiesta}
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-soft mb-1">
              {t('richieste.rispondi.risposta')} <span className="text-accent-danger">*</span>
            </label>
            <textarea
              className={inputCls}
              rows={5}
              value={commento}
              onChange={(e) => setCommento(e.target.value)}
              placeholder={t('richieste.rispondi.risposta_placeholder')}
            />
          </div>

          <p className="text-xs text-ink-soft">
            {t('richieste.rispondi.ritorno_stato', { stato: consultazione.stato_pre_consultazione ?? '—' })}
          </p>

          {error && (
            <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50"
          >
            {t('common.azioni.annulla')}
          </button>
          <button
            type="button"
            onClick={handleRispondi}
            disabled={loading || !commento.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60"
          >
            {loading ? t('richieste.rispondi.invio_in_corso') : t('richieste.rispondi.btn_invia')}
          </button>
        </div>
      </div>
    </div>
  );
}
